/**
 * 法務・案内系ページ（プライバシーポリシー / 未成年者同意書 / 医療広告に関する表記 / 採用情報）
 * の見出し・本文データを1か所で管理する。
 *
 * 重要：
 * 法律・規約・同意事項・医療広告に関する「本文」は、正式文が確定するまで空のままにしておく。
 * 正式文が決まったら、該当ページの sections（または recruitPositions / minorConsentPdfUrl）に追記するだけで
 * ページへ反映される。AI判断で法律文を生成しないこと。
 */

export type LegalBlock =
  | { type: "paragraph"; text: string }
  | { type: "list"; items: string[] }
  | { type: "info"; lines: string[] };

export type LegalSection = {
  /** アンカー用のid（ページ内リンクに使用） */
  id: string;
  /** 見出し（空文字の場合は見出しを表示しない） */
  heading: string;
  /**
   * 本文段落。正式文が未確定の間は空配列にしておく（画面では控えめに「—」を表示）。
   */
  paragraphs: string[];
  /**
   * 段落と番号付きリストを順序どおりに混在させたい場合に使用する。
   * 指定されている場合は content を本文として優先表示し、paragraphs は使用しない。
   */
  content?: LegalBlock[];
};

export type LegalPageMeta = {
  /** ページのパス（内部リンク・パンくず用） */
  path: string;
  en: string;
  h1: string;
  /** PAGE HERO に表示する短い説明（法律内容を含まない中立な案内文のみ） */
  lead: string;
  /** PAGE HERO のリード補足（必要なページのみ。未指定の場合は表示しない） */
  sub?: string;
  title: string;
  description: string;
};

/* --------------------------------------------------------------------------
 * ページ基本情報（EN / H1 / リード / SEO）
 * ------------------------------------------------------------------------ */
export const legalPages: {
  privacy: LegalPageMeta;
  minorConsent: LegalPageMeta;
  medicalAdvertising: LegalPageMeta;
  recruit: LegalPageMeta;
} = {
  privacy: {
    path: "/privacy",
    en: "PRIVACY POLICY",
    h1: "プライバシーポリシー",
    lead: "当院における個人情報の取り扱いについてご案内します。",
    title: "プライバシーポリシー｜渋谷美容皮膚科クリニック",
    description:
      "渋谷美容皮膚科クリニックのプライバシーポリシーです。当院における個人情報の取り扱いについてご案内します。",
  },
  minorConsent: {
    path: "/minor-consent",
    en: "FOR MINORS",
    h1: "未成年の方へ",
    lead: "渋谷美容皮膚科クリニックでは、18歳未満の方が施術をお申し込みになる場合、親権者の同意をお願いしております。",
    sub: "年齢によって必要なお手続きが異なりますので、ご予約・ご来院前に下記をご確認ください。",
    title: "未成年の方へ｜渋谷美容皮膚科クリニック",
    description:
      "18歳未満の方の施術に必要な親権者同意書や、年齢別のお手続きについてご案内します。15歳以下の方は親権者の同伴が必要です。",
  },
  medicalAdvertising: {
    path: "/medical-advertising",
    en: "MEDICAL ADVERTISING",
    h1: "医療広告に関する表記",
    lead: "当院の美容医療に関する広告表示についてご案内します。",
    title: "医療広告に関する表記｜渋谷美容皮膚科クリニック",
    description:
      "渋谷美容皮膚科クリニックの医療広告に関する表記についてご案内します。美容医療の広告表示に関する考え方を掲載しています。",
  },
  recruit: {
    path: "/recruit",
    en: "RECRUIT",
    h1: "採用情報",
    lead: "美容医療を通じて、\n患者様の「変わりたい」に寄り添う仕事。",
    sub: "渋谷美容皮膚科クリニックでは、患者様一人ひとりに向き合い、より良い美容医療をともにつくる仲間を募集しています。",
    title: "採用情報｜渋谷美容皮膚科クリニック",
    description:
      "渋谷美容皮膚科クリニックの採用情報です。美容医療カウンセラーの仕事内容、募集要項、選考の流れをご案内します。",
  },
};

/* --------------------------------------------------------------------------
 * プライバシーポリシー本文
 * 前文（見出しなし）→ 第1条〜第10条 を content の段落・番号付きリストで構成する。
 * 制定日・改定日は正式日が決まるまで表示しない（本文末尾に日付を置かない）。
 * ------------------------------------------------------------------------ */
export const privacyPolicySections: LegalSection[] = [
  {
    id: "privacy-intro",
    heading: "",
    paragraphs: [],
    content: [
      {
        type: "paragraph",
        text: "渋谷美容皮膚科クリニック（以下「当院」といいます。）は、患者様その他当院をご利用いただく皆様の個人情報を適切に取り扱うことを重要な責務と考え、個人情報の保護に関する法律その他の関係法令等を遵守し、以下のとおり個人情報の適切な管理に努めます。",
      },
    ],
  },
  {
    id: "privacy-1",
    heading: "第1条　個人情報の取得",
    paragraphs: [],
    content: [
      {
        type: "paragraph",
        text: "当院は、診療、カウンセリング、予約、各種お問い合わせその他当院のサービス提供に必要な範囲で、適正かつ適法な方法により個人情報を取得します。",
      },
      {
        type: "paragraph",
        text: "取得する情報には、氏名、生年月日、性別、住所、電話番号、メールアドレス、診療・施術に必要な情報、予約情報、お問い合わせ内容その他当院のサービス提供に必要な情報が含まれる場合があります。",
      },
    ],
  },
  {
    id: "privacy-2",
    heading: "第2条　個人情報の利用目的",
    paragraphs: [],
    content: [
      {
        type: "paragraph",
        text: "当院は、取得した個人情報を、以下の目的のために利用します。",
      },
      {
        type: "list",
        items: [
          "診療、施術、カウンセリングその他医療サービスの提供のため",
          "予約の受付、確認、変更その他予約管理のため",
          "患者様からのお問い合わせ、ご相談等への対応のため",
          "診療、施術その他当院のサービスに関する必要なご連絡のため",
          "会計、料金請求その他診療に付随する事務処理のため",
          "当院のサービス品質向上、業務改善および院内管理のため",
          "法令その他の規定に基づく対応のため",
          "その他、上記各号に付随する目的のため",
        ],
      },
      {
        type: "paragraph",
        text: "上記以外の目的で個人情報を利用する必要が生じた場合は、法令上認められる場合を除き、必要に応じてご本人の同意を得るものとします。",
      },
    ],
  },
  {
    id: "privacy-3",
    heading: "第3条　個人情報の管理",
    paragraphs: [],
    content: [
      {
        type: "paragraph",
        text: "当院は、取り扱う個人情報について、漏えい、滅失、毀損、不正アクセスその他の不適切な取扱いを防止するため、必要かつ適切な安全管理措置を講じるよう努めます。",
      },
      {
        type: "paragraph",
        text: "また、個人情報を取り扱う職員その他の関係者に対し、適切な管理および取扱いを行うよう必要な監督を行います。",
      },
    ],
  },
  {
    id: "privacy-4",
    heading: "第4条　個人情報の第三者提供",
    paragraphs: [],
    content: [
      {
        type: "paragraph",
        text: "当院は、次の場合を除き、ご本人の同意なく個人情報を第三者に提供しません。",
      },
      {
        type: "list",
        items: [
          "法令に基づく場合",
          "人の生命、身体または財産の保護のために必要がある場合で、ご本人の同意を得ることが困難であるとき",
          "公衆衛生の向上または児童の健全な育成の推進のために特に必要がある場合で、ご本人の同意を得ることが困難であるとき",
          "国の機関、地方公共団体その他法令の定める者が、法令に定める事務を遂行することに対して協力する必要がある場合",
          "その他、法令により認められている場合",
        ],
      },
    ],
  },
  {
    id: "privacy-5",
    heading: "第5条　業務委託",
    paragraphs: [],
    content: [
      {
        type: "paragraph",
        text: "当院は、予約管理、システム運用その他当院の業務を遂行するために必要な範囲で、個人情報の取扱いを外部事業者へ委託する場合があります。",
      },
      {
        type: "paragraph",
        text: "この場合、当院は委託先を適切に選定し、必要かつ適切な監督を行うよう努めます。",
      },
    ],
  },
  {
    id: "privacy-6",
    heading: "第6条　個人情報の開示・訂正・利用停止等",
    paragraphs: [],
    content: [
      {
        type: "paragraph",
        text: "ご本人から、当院が保有する個人情報について、開示、訂正、追加、削除、利用停止その他法令に基づく請求があった場合には、ご本人であることを確認したうえで、法令に従い適切に対応します。",
      },
      {
        type: "paragraph",
        text: "なお、法令により開示等を行わないことが認められている場合には、ご希望に沿えないことがあります。",
      },
    ],
  },
  {
    id: "privacy-7",
    heading: "第7条　Cookie等の利用",
    paragraphs: [],
    content: [
      {
        type: "paragraph",
        text: "当院のウェブサイトでは、サイトの利便性向上、利用状況の把握、サービス改善等を目的として、Cookieその他これに類する技術を使用する場合があります。",
      },
      {
        type: "paragraph",
        text: "Cookieは、ウェブサイトを閲覧した端末を識別するための情報であり、通常、Cookieそのものから直接個人を特定するものではありません。",
      },
      {
        type: "paragraph",
        text: "ブラウザの設定によりCookieの受け入れを制限または無効にすることができますが、その場合、ウェブサイトの一部機能をご利用いただけない場合があります。",
      },
    ],
  },
  {
    id: "privacy-8",
    heading: "第8条　外部サービスについて",
    paragraphs: [],
    content: [
      {
        type: "paragraph",
        text: "当院ウェブサイトから外部のウェブサイトまたはサービスへ移動した場合、移動先における個人情報の取扱いについては、各サービス提供者が定めるプライバシーポリシーその他の規定が適用されます。",
      },
      {
        type: "paragraph",
        text: "当院ウェブサイトからリンクされた外部サービスをご利用になる場合は、各サービスの個人情報の取扱いについてもご確認ください。",
      },
    ],
  },
  {
    id: "privacy-9",
    heading: "第9条　プライバシーポリシーの変更",
    paragraphs: [],
    content: [
      {
        type: "paragraph",
        text: "当院は、法令の改正、サービス内容の変更その他必要に応じて、本プライバシーポリシーの内容を変更することがあります。",
      },
      {
        type: "paragraph",
        text: "変更後のプライバシーポリシーは、当院ウェブサイトへの掲載その他適切な方法により公表します。",
      },
    ],
  },
  {
    id: "privacy-10",
    heading: "第10条　お問い合わせ窓口",
    paragraphs: [],
    content: [
      {
        type: "paragraph",
        text: "個人情報の取扱いに関するお問い合わせは、下記までお願いいたします。",
      },
      {
        type: "info",
        lines: [
          "渋谷美容皮膚科クリニック",
          "〒150-0043",
          "東京都渋谷区道玄坂2-6-14 HYビル7F",
          "電話番号：03-6416-4392",
          "診療時間：11:00～20:00",
        ],
      },
    ],
  },
];

/* --------------------------------------------------------------------------
 * 医療広告に関する表記 本文
 * 前文（見出しなし）→ 第1条〜第10条 を content の段落・連絡先で構成する。
 * 未承認医薬品・医療機器の具体的な製剤名・機器名・入手経路・海外承認情報・副作用発生率等は
 * 実際の使用内容が確定するまで本文に追加しない（各治療ページ側で正式化する）。
 * ------------------------------------------------------------------------ */
export const medicalAdvertisingSections: LegalSection[] = [
  {
    id: "ma-intro",
    heading: "",
    paragraphs: [],
    content: [
      {
        type: "paragraph",
        text: "渋谷美容皮膚科クリニック（以下「当院」といいます。）では、患者様が美容医療について適切に理解し、治療・施術をご検討いただけるよう、当院ウェブサイトに掲載する情報の適正な表示に努めています。",
      },
      {
        type: "paragraph",
        text: "当院ウェブサイトに掲載する医療情報については、医療法その他の関係法令および医療広告に関する指針等を踏まえ、治療内容、費用、主なリスク・副作用等について適切な情報提供を行うよう努めます。",
      },
    ],
  },
  {
    id: "ma-1",
    heading: "第1条　自由診療について",
    paragraphs: [],
    content: [
      {
        type: "paragraph",
        text: "当院ウェブサイトに掲載している美容医療の多くは、公的医療保険が適用されない自由診療です。",
      },
      {
        type: "paragraph",
        text: "自由診療に該当する治療・施術については、治療内容や費用等をご確認いただいたうえで、医師の診察および説明を受け、十分にご理解いただいてから治療をご検討ください。",
      },
    ],
  },
  {
    id: "ma-2",
    heading: "第2条　治療の効果および経過について",
    paragraphs: [],
    content: [
      {
        type: "paragraph",
        text: "治療・施術による効果、治療経過、必要な回数、ダウンタイムその他の結果には個人差があります。",
      },
      {
        type: "paragraph",
        text: "当院ウェブサイトに掲載する情報は、すべての患者様に同一の効果や結果を保証するものではありません。",
      },
      {
        type: "paragraph",
        text: "実際の治療内容については、患者様の状態やご希望等を確認したうえで、医師が診察し、適応を判断します。",
      },
    ],
  },
  {
    id: "ma-3",
    heading: "第3条　治療に伴うリスク・副作用について",
    paragraphs: [],
    content: [
      {
        type: "paragraph",
        text: "美容医療を含む医療行為には、治療内容に応じたリスク、副作用、合併症その他の注意事項があります。",
      },
      {
        type: "paragraph",
        text: "当院では、各治療ページ等において主なリスク・副作用等を掲載するとともに、診察時に必要な説明を行うよう努めます。",
      },
      {
        type: "paragraph",
        text: "治療をご検討の際は、期待される効果だけでなく、リスク・副作用等についてもご確認ください。",
      },
    ],
  },
  {
    id: "ma-4",
    heading: "第4条　治療費用について",
    paragraphs: [],
    content: [
      {
        type: "paragraph",
        text: "自由診療の費用は、治療内容、施術部位、使用する製剤その他の条件によって異なる場合があります。",
      },
      {
        type: "paragraph",
        text: "当院ウェブサイトに料金を掲載する場合は、患者様が治療をご検討するために必要な情報をできる限り分かりやすく表示するよう努めます。",
      },
      {
        type: "paragraph",
        text: "実際に必要となる費用については、カウンセリングまたは診察時にご確認ください。",
      },
    ],
  },
  {
    id: "ma-5",
    heading: "第5条　未承認医薬品・医療機器等について",
    paragraphs: [],
    content: [
      {
        type: "paragraph",
        text: "当院で取り扱う治療の中には、国内において承認されていない医薬品・医療機器等を使用する治療が含まれる場合があります。",
      },
      {
        type: "paragraph",
        text: "該当する治療については、必要に応じて、未承認であること、入手経路、国内の承認医薬品等の有無、諸外国における安全性等に係る情報その他必要な事項について、各治療ページまたは診察時に情報提供を行います。",
      },
      {
        type: "paragraph",
        text: "具体的な製剤名・医療機器名・入手経路等については、当院で実際に使用する内容を確認したうえで掲載します。",
      },
    ],
  },
  {
    id: "ma-6",
    heading: "第6条　治療内容等の変更について",
    paragraphs: [],
    content: [
      {
        type: "paragraph",
        text: "当院ウェブサイトに掲載している治療内容、料金、使用する医薬品・医療機器その他の情報は、診療体制、取扱内容その他の事情により変更となる場合があります。",
      },
      {
        type: "paragraph",
        text: "最新の治療内容については、ご予約時またはご来院時にご確認ください。",
      },
    ],
  },
  {
    id: "ma-7",
    heading: "第7条　症例写真等について",
    paragraphs: [],
    content: [
      {
        type: "paragraph",
        text: "当院ウェブサイトに症例写真その他の治療事例を掲載する場合は、患者様ご本人の同意を得たうえで掲載します。",
      },
      {
        type: "paragraph",
        text: "症例写真等は特定の患者様における治療経過を示すものであり、すべての患者様に同一の結果を保証するものではありません。",
      },
      {
        type: "paragraph",
        text: "症例を掲載する場合は、治療内容、費用、主なリスク・副作用等についても必要な情報を併せて表示するよう努めます。",
      },
    ],
  },
  {
    id: "ma-8",
    heading: "第8条　体験談・口コミ等について",
    paragraphs: [],
    content: [
      {
        type: "paragraph",
        text: "患者様個人の体験談や口コミ等は、個人の感想であり、治療の効果や結果を保証するものではありません。",
      },
      {
        type: "paragraph",
        text: "当院がウェブサイト上で患者様の体験等を取り扱う場合は、関係法令および医療広告に関する指針等を踏まえ、適切な取扱いに努めます。",
      },
    ],
  },
  {
    id: "ma-9",
    heading: "第9条　当院ウェブサイトの情報について",
    paragraphs: [],
    content: [
      {
        type: "paragraph",
        text: "当院ウェブサイトに掲載する情報は、患者様が治療・施術を検討する際の一般的な情報提供を目的としています。",
      },
      {
        type: "paragraph",
        text: "ウェブサイト上の情報のみで診断や治療の適否を判断することはできません。",
      },
      {
        type: "paragraph",
        text: "治療をご希望の場合は、医師の診察を受け、治療内容、費用、リスク・副作用等について説明を受けたうえでご検討ください。",
      },
    ],
  },
  {
    id: "ma-10",
    heading: "第10条　お問い合わせ",
    paragraphs: [],
    content: [
      {
        type: "paragraph",
        text: "当院ウェブサイトに掲載している治療内容、料金その他の情報についてご不明な点がございましたら、当院までお問い合わせください。",
      },
      {
        type: "info",
        lines: [
          "渋谷美容皮膚科クリニック",
          "〒150-0043",
          "東京都渋谷区道玄坂2-6-14 HYビル7F",
          "電話番号：03-6416-4392",
          "診療時間：11:00～20:00",
        ],
      },
    ],
  },
];

/* --------------------------------------------------------------------------
 * 未成年者同意書（/minor-consent）
 * 年齢別のご案内 → 親権者同意書について → ご来院までの流れ → 注意事項 → FAQ
 * 患者向けの案内文のみを掲載する。年齢条件を推測して追加しないこと。
 * ------------------------------------------------------------------------ */

/** 年齢別のご案内（PC 3カード / スマホ 縦並び） */
export const minorConsentAgeGroups = [
  {
    id: "under-15",
    age: "15歳以下",
    consent: "必要",
    accompany: "必要",
    description:
      "親権者同意書の提出と、親権者の同伴が必要です。カウンセリング・施術をご希望の場合は、親権者の方と一緒にご来院ください。",
    emphasis: true,
  },
  {
    id: "16-18",
    age: "16歳以上18歳未満",
    consent: "必要",
    accompany: "原則不要",
    description:
      "親権者同意書の提出が必要です。親権者の同伴は原則必須ではありませんが、必要に応じて親権者の方へお電話で確認させていただく場合があります。",
    emphasis: false,
  },
  {
    id: "over-18",
    age: "18歳以上",
    consent: "不要",
    accompany: "不要",
    description:
      "原則として、親権者同意書の提出および親権者の同伴は必要ありません。",
    emphasis: false,
  },
];

/** 親権者同意書について の本文 */
export const minorConsentParagraphs = [
  "18歳未満の方は、親権者ご本人が記入・署名した親権者同意書をご用意ください。",
  "同意書をダウンロード・印刷し、必要事項をご記入のうえ、ご来院時に原本をお持ちください。",
  "施術を受けるご本人による親権者署名の代筆はお受けできません。",
];

/** ご来院までの流れ（4 STEP） */
export const minorConsentSteps = [
  { no: "01", title: "同意書をダウンロード", text: "親権者同意書をダウンロードし、印刷してください。" },
  { no: "02", title: "親権者が記入・署名", text: "親権者ご本人が必要事項を記入し、署名してください。" },
  { no: "03", title: "ご来院時に持参", text: "記入済みの親権者同意書の原本を受付へご提出ください。" },
  {
    no: "04",
    title: "カウンセリング・診察",
    text: "内容を確認したうえで、医師の診察を行います。16歳以上18歳未満の方は、必要に応じて親権者へ電話確認を行う場合があります。",
  },
];

/** 注意事項 */
export const minorConsentNotes = [
  "親権者同意書は、親権者ご本人がご記入・ご署名ください。",
  "記載内容に不備がある場合や親権者の同意を確認できない場合は、当日のカウンセリングまたは施術をお受けいただけない場合があります。",
  "施術内容や診察結果によっては、親権者の同伴をお願いする場合があります。",
  "診察の結果、施術をお受けいただけない場合があります。",
  "お申し込みいただく施術内容が変更となる場合は、改めて親権者の同意をお願いすることがあります。",
];

/** FAQ */
export const minorConsentFaqs = [
  {
    id: "minor-faq-1",
    q: "18歳ですが、親権者同意書は必要ですか？",
    a: "18歳以上の方は、原則として親権者同意書の提出および親権者の同伴は必要ありません。",
  },
  {
    id: "minor-faq-2",
    q: "16歳ですが、親権者の同伴は必要ですか？",
    a: "16歳以上18歳未満の方は、親権者同意書をご持参いただければ、原則として親権者の同伴は必須ではありません。ただし、必要に応じて親権者へ電話確認を行う場合があります。",
  },
  {
    id: "minor-faq-3",
    q: "同意書を忘れた場合でも施術できますか？",
    a: "18歳未満の方で必要な親権者同意書をご提出いただけない場合、当日の施術をお受けいただけない場合があります。",
  },
];

/**
 * 親権者同意書PDFのURL。
 * 正式なPDFファイル（SBDC_parental_consent_form.pdf）がアップロードされるまでは空文字のままにしておく。
 * 存在しないPDF URLをここへ仮置きしないこと。アップロード後にこの定数へ実URLを設定するだけでボタンが有効になる。
 */
export const minorConsentPdfUrl =
  "/documents/SBDC_parental_consent_form.pdf";
export const minorConsentPdfFileName = "SBDC_parental_consent_form.pdf";

/* --------------------------------------------------------------------------
 * 採用情報 本文
 * MESSAGE → WORK → ENVIRONMENT → REQUIREMENTS（＋求める人物像）→ SELECTION FLOW → ENTRY
 *
 * 重要：
 * 固定報酬・歩合・達成報酬等の内部報酬制度、および架空の応募フォーム・採用専用メールアドレスは
 * 掲載しない。LINEは採用ページでは使用しない（採用導線は電話CTAのみ）。
 * ------------------------------------------------------------------------ */
export const recruitMessage = {
  en: "MESSAGE",
  heading: "一つひとつの経験を、\n確かな成長へ。",
  paragraphs: [
    "美容医療の仕事に求められるのは、知識や技術だけではありません。",
    "患者様が抱えているお悩みやご希望を丁寧に伺い、一人ひとりに向き合う姿勢を大切にしています。",
    "SBDCでは、これまで培ってきた経験を活かしながら、新しい知識や技術を学び、スタッフ一人ひとりが成長できる環境づくりを目指しています。",
  ],
};

export const recruitWork = {
  en: "WORK",
  heading: "仕事内容",
  positionLabel: "現在募集する職種",
  position: "美容医療カウンセラー",
  duties: [
    "受付・患者様対応",
    "美容医療カウンセリング",
    "施術や料金プランのご案内",
    "予約管理",
    "看護師・医師の診療サポート",
    "看護助手業務",
    "その他クリニック運営に付随する業務",
  ],
  notes: [
    "患者様のお悩みやご希望を伺い、必要な情報を分かりやすくご案内することが、カウンセラーの大切な役割です。",
    "医師・看護師・スタッフと連携しながら、患者様が安心して診療を受けられる環境づくりを行います。",
  ],
};

export const recruitEnvironment = {
  en: "ENVIRONMENT",
  heading: "SBDCで働く",
  items: [
    {
      no: "01",
      title: "美容医療の知識を深める",
      text: "患者様へのご案内に必要な美容医療の知識や施術について学びながら、実務経験を積んでいきます。",
    },
    {
      no: "02",
      title: "経験を活かせる環境",
      text: "美容医療、エステ、美容部員、カウンセリング、営業など、これまでの経験を活かしていただけます。",
    },
    {
      no: "03",
      title: "チームで患者様を支える",
      text: "カウンセラーだけで完結するのではなく、医師・看護師・スタッフが連携し、患者様の診療をサポートします。",
    },
  ],
};

export const recruitRequirements = {
  en: "REQUIREMENTS",
  heading: "募集要項",
  rows: [
    { label: "職種", value: "美容医療カウンセラー" },
    { label: "契約形態", value: "業務委託" },
    { label: "勤務地", value: "渋谷美容皮膚科クリニック" },
    { label: "所在地", value: "〒150-0043\n東京都渋谷区道玄坂2-6-14 HYビル7F" },
    { label: "勤務時間", value: "11:00～20:00\n（実働8時間・休憩60分）" },
    { label: "休日", value: "月8日を目安" },
    { label: "仕事内容", value: "受付／カウンセリング／看護助手／その他付随業務" },
    { label: "応募資格", value: "美容医療・エステ・美容部員・営業等の経験者歓迎" },
    { label: "報酬", value: "詳細は選考時にご案内します。" },
  ],
};

export const recruitPersona = {
  heading: "求める人物像",
  items: [
    "患者様に丁寧に向き合える方",
    "美容医療への関心がある方",
    "新しい知識を積極的に学べる方",
    "周囲と連携して業務を進められる方",
    "これまでの経験を活かして成長したい方",
  ],
};

export const recruitSelectionFlow = {
  en: "SELECTION FLOW",
  heading: "選考の流れ",
  steps: [
    { no: "01", title: "ご応募", note: "" },
    { no: "02", title: "書類選考", note: "" },
    { no: "03", title: "一次面接", note: "" },
    { no: "04", title: "二次面接", note: "※実施しない場合があります。" },
    { no: "05", title: "内定", note: "" },
    { no: "06", title: "勤務開始に向けた手続き", note: "" },
  ],
};

export const recruitEntry = {
  en: "ENTRY",
  heading: "応募について",
  paragraphs: ["募集状況や応募方法については、当院までお問い合わせください。"],
};